import React, { Dispatch } from 'react';
import { eventsReadyForCheckIn } from '../components/arrival/arrival-utils';
import { EventData, TicketData } from '../types';

type ArrivalAdminTabValue = 'settings' | 'scan' | 'attendees' | null;
// State
type AppState = {
  drawerOpen: boolean;
  // How many mounted pages/lazy sections are loading; the shell shows the
  // 3px bar under the App Bar while this is above 0
  routeLoadingCount: number;
  // Bumped by "Try again" on a page that didn't load, to remount it
  routeKey: number;
  eventsLoading: boolean;
  eventsData: EventData[];
  arrival: {
    events: EventData[];
    admin: {
      tabValue: ArrivalAdminTabValue;
      selected: EventData | null;
      ticket: TicketData | null;
    };
  };
};
const initialState: AppState = {
  drawerOpen: false,
  routeLoadingCount: 0,
  routeKey: 0,
  eventsLoading: true,
  eventsData: [],
  arrival: {
    events: [],
    admin: {
      tabValue: null,
      selected: null,
      ticket: null,
    },
  },
};

// Actions
type AppActions =
  | { type: 'TOGGLE_DRAWER' }
  | { type: 'DRAWER_CLOSE' }
  | { type: 'ROUTE_LOADING_START' }
  | { type: 'ROUTE_LOADING_END' }
  | { type: 'RETRY_ROUTE' }
  | { type: 'SET_EVENTS'; payload: EventData[] }
  | { type: 'SET_ARVL_ADMN_TAB'; payload: ArrivalAdminTabValue }
  | { type: 'SET_ARVL_ADMN_SELECT'; payload: EventData | null }
  | { type: 'SET_ARVL_ADMN_TICKET'; payload: TicketData | null };

const reducer = (state: AppState, action: AppActions) => {
  switch (action.type) {
    case 'TOGGLE_DRAWER': {
      return { ...state, drawerOpen: !state.drawerOpen };
    }
    case 'DRAWER_CLOSE': {
      return { ...state, drawerOpen: false };
    }
    case 'ROUTE_LOADING_START': {
      return { ...state, routeLoadingCount: state.routeLoadingCount + 1 };
    }
    case 'ROUTE_LOADING_END': {
      return {
        ...state,
        routeLoadingCount: Math.max(0, state.routeLoadingCount - 1),
      };
    }
    case 'RETRY_ROUTE': {
      return { ...state, routeKey: state.routeKey + 1 };
    }
    case 'SET_EVENTS': {
      const arrivalEvents = eventsReadyForCheckIn({ data: action.payload });

      return {
        ...state,
        eventsLoading: false,
        eventsData: action.payload,
        arrival: {
          ...state.arrival,
          events: arrivalEvents,
        },
      };
    }
    case 'SET_ARVL_ADMN_TAB': {
      return {
        ...state,
        arrival: {
          ...state.arrival,
          admin: {
            ...state.arrival.admin,
            tabValue: action.payload,
          },
        },
      };
    }
    case 'SET_ARVL_ADMN_SELECT': {
      return {
        ...state,
        arrival: {
          ...state.arrival,
          admin: {
            ...state.arrival.admin,
            selected: action.payload,
          },
        },
      };
    }
    case 'SET_ARVL_ADMN_TICKET': {
      return {
        ...state,
        arrival: {
          ...state.arrival,
          admin: {
            ...state.arrival.admin,
            ticket: action.payload,
          },
        },
      };
    }
    default: {
      return { ...state };
    }
  }
};

export const AppContext = React.createContext(
  {} as {
    state: AppState;
    dispatch: Dispatch<AppActions>;
  },
);

export function AppProvider(props: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {props.children}
    </AppContext.Provider>
  );
}
